import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, ArrowRight, MapPin } from "lucide-react";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function CitiesIndexPage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [tab, setTab] = useState("with_cafes"); // with_cafes | all

  useDocumentMeta({
    title: "Browse cities across the US & Canada",
    description: "Find specialty cafés and bean shops in 450 cities across the United States and Canada. Every city catalogued — from major metros to small towns.",
  });

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (country !== "all") params.country = country;
    api.get("/cities", { params })
      .then(({ data }) => setCities(data))
      .finally(() => setLoading(false));
  }, [country]);

  const filtered = useMemo(() => {
    let out = cities;
    if (tab === "with_cafes") out = out.filter((c) => c.count > 0);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter(
        (c) => c.name.toLowerCase().includes(needle) ||
               (c.region || "").toLowerCase().includes(needle)
      );
    }
    return out;
  }, [cities, q, tab]);

  const withCafes = useMemo(() => cities.filter((c) => c.count > 0).length, [cities]);
  const empty = cities.length - withCafes;

  return (
    <div data-testid="cities-index-page" className="pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">From coast to coast</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
          Find good coffee in <em className="italic font-normal">any</em> city.
        </h1>
        <p className="text-[#786B63] mt-3 max-w-2xl">
          {cities.length.toLocaleString()} cities catalogued across the US and Canada — {withCafes} already have community-submitted spots, {empty} are waiting for their first listing.
        </p>

        <div className="mt-8 border border-border bg-card rounded-sm p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#786B63]" strokeWidth={1.5} />
            <Input
              data-testid="cities-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by city or state/province…"
              className="pl-9 rounded-sm bg-[#F9F6F0] border-border h-11"
            />
          </div>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="rounded-sm bg-[#F9F6F0] h-11 w-[180px]" data-testid="cities-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#F9F6F0]">
              <SelectItem value="all">All countries</SelectItem>
              <SelectItem value="USA">United States</SelectItem>
              <SelectItem value="Canada">Canada</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex border border-border rounded-sm overflow-hidden bg-[#F9F6F0] h-11 ml-auto">
            <button
              type="button"
              onClick={() => setTab("with_cafes")}
              data-testid="cities-tab-with-cafes"
              className={`px-4 text-xs uppercase tracking-wider ${tab === "with_cafes" ? "bg-[#2C1E16] text-[#F9F6F0]" : "text-[#2C1E16]"}`}
            >
              With cafés
            </button>
            <button
              type="button"
              onClick={() => setTab("all")}
              data-testid="cities-tab-all"
              className={`px-4 text-xs uppercase tracking-wider ${tab === "all" ? "bg-[#2C1E16] text-[#F9F6F0]" : "text-[#2C1E16]"}`}
            >
              All cities
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-8">
        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="border border-border bg-card h-20 rounded-sm animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-16 text-center">
            <p className="font-serif text-2xl">No matches.</p>
            <p className="text-sm text-[#786B63] mt-2">Try a different search or country.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((c) => (
              <Link
                key={c.slug}
                to={`/city/${c.slug}`}
                data-testid={`city-row-${c.slug}`}
                className="group border border-border bg-card rounded-sm p-4 hover:-translate-y-0.5 transition-transform flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-serif text-xl leading-tight truncate">{c.name}</p>
                  <p className="text-xs text-[#786B63] mt-1 inline-flex items-center gap-1.5">
                    <MapPin size={11} strokeWidth={1.5} />
                    {c.region ? `${c.region}, ` : ""}{c.country}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {c.count > 0 ? (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#C05A46] text-[#F9F6F0] rounded-full">
                      {c.count} spot{c.count === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#786B63]">
                      No cafés yet
                    </span>
                  )}
                  <ArrowRight className="h-3 w-3 text-[#786B63] group-hover:text-[#C05A46] mt-1.5 ml-auto" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
